import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { fetchCommunityBySlug } from "@/services/communityService";

const CommunityDebug = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [results, setResults] = useState<any>({});
  const [loading, setLoading] = useState<string>("");

  const testMentorProfile = async () => {
    setLoading("mentor");
    try {
      console.log("🧪 [Debug] Testando busca completa do perfil do mentor...");
      
      // ID do mentor Heitor
      const mentorId = 'b0e6f620-12b2-487c-a55c-60754ae4d9b9';
      
      // Primeiro, vamos tentar descobrir quais campos existem
      console.log("🔍 [Debug] Testando campos individuais...");
      
      // Teste 1: apenas id
      const { data: testId, error: errorId } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', mentorId)
        .single();
      console.log("✅ [Debug] Campo 'id' existe:", !!testId, errorId?.message || 'OK');
      
      // Teste 2: nickname
      const { data: testNickname, error: errorNickname } = await supabase
        .from('profiles')
        .select('nickname')
        .eq('id', mentorId)
        .single();
      console.log("✅ [Debug] Campo 'nickname' existe:", !!testNickname, errorNickname?.message || 'OK');
      
      // Teste 3: slug
      const { data: testSlug, error: errorSlug } = await supabase
        .from('profiles')
        .select('slug')
        .eq('id', mentorId)
        .single();
      console.log("✅ [Debug] Campo 'slug' existe:", !!testSlug, errorSlug?.message || 'OK');
      
      // Teste 4: avatar_url
      const { data: testAvatar, error: errorAvatar } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('id', mentorId)
        .single();
      console.log("✅ [Debug] Campo 'avatar_url' existe:", !!testAvatar, errorAvatar?.message || 'OK');
      
      // Teste 5: image_url
      const { data: testImage, error: errorImage } = await supabase
        .from('profiles')
        .select('image_url')
        .eq('id', mentorId)
        .single();
      console.log("✅ [Debug] Campo 'image_url' existe:", !!testImage, errorImage?.message || 'OK');
      
      // Agora buscar tudo junto com campos que sabemos que existem
      const { data: mentorProfile, error } = await supabase
        .from('profiles')
        .select('*')  // Buscar TODOS os campos disponíveis
        .eq('id', mentorId)
        .single();
      
      if (error) {
        console.error("❌ [Debug] Erro ao buscar perfil do mentor:", error);
        throw error;
      }
      
      console.log("✅ [Debug] Perfil completo do mentor encontrado:", mentorProfile);
      console.log("✅ [Debug] Campos disponíveis:", Object.keys(mentorProfile));
      console.log("🔗 [Debug] Slug do mentor:", mentorProfile.slug);
      console.log("📸 [Debug] Avatar URL:", (mentorProfile as any).avatar_url);
      console.log("📸 [Debug] Image URL:", mentorProfile.image_url);
      
      // Gerar URLs
      const mentorUrls = {
        currentWrong: `/mentor/${mentorProfile.nickname}`,  // URL atual incorreta
        shouldBe: `/mentores/${mentorProfile.slug}`,        // URL correta
        fallback1: `/mentores/${mentorProfile.nickname}`,   // Fallback 1
        fallback2: `/mentores/${mentorProfile.id}`          // Fallback 2
      };
      
      console.log("🔗 [Debug] URLs do mentor:", mentorUrls);
      
      setResults(prev => ({ 
        ...prev, 
        mentorProfile: { 
          data: mentorProfile,
          urls: mentorUrls,
          availableFields: Object.keys(mentorProfile)
        } 
      }));
      
      toast({
        title: "Sucesso",
        description: `Perfil do mentor carregado! Slug: ${mentorProfile.slug}`,
      });
    } catch (error) {
      console.error("💥 [Debug] Erro geral:", error);
      setResults(prev => ({ ...prev, mentorProfile: { error } }));
      toast({
        title: "Erro",
        description: "Falha ao carregar perfil do mentor",
        variant: "destructive",
      });
    }
    setLoading("");
  };

  const testCommunityBySlug = async () => {
    setLoading("community");
    try {
      console.log("🧪 [Debug] Testando fetchCommunityBySlug...");
      
      const community = await fetchCommunityBySlug('rumo-ao-worlds-2025-pokemon-tcg');
      
      if (community) {
        console.log("✅ [Debug] Comunidade encontrada:", community);
        console.log("👤 [Debug] Dados do mentor na comunidade:", community.mentor);
        console.log("📸 [Debug] Avatar do mentor:", community.mentor?.avatar_url);
        console.log("📸 [Debug] Image URL do mentor:", community.mentor?.image_url);
        console.log("🔗 [Debug] Slug do mentor:", community.mentor?.slug);
        
        setResults(prev => ({ ...prev, community: { data: community } }));
        toast({
          title: "Sucesso",
          description: "Comunidade carregada com sucesso!",
        });
      } else {
        console.log("❌ [Debug] Comunidade não encontrada");
        setResults(prev => ({ ...prev, community: { error: 'Não encontrada' } }));
        toast({
          title: "Erro",
          description: "Falha ao carregar comunidade",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("💥 [Debug] Erro geral:", error);
      setResults(prev => ({ ...prev, community: { error } }));
      toast({
        title: "Erro",
        description: "Falha ao carregar comunidade",
        variant: "destructive",
      });
    }
    setLoading("");
  };

  const testGameQuery = async () => {
    setLoading("game");
    try {
      console.log("🧪 [Debug] Testando query de games...");
      
      const { data: games, error } = await supabase
        .from('games')
        .select('*')
        .limit(5);
      
      if (error) {
        console.error("💥 [Debug] Erro geral na query de game:", error);
        throw error;
      }
      
      console.log("✅ [Debug] Jogos encontrados:", games);
      setResults(prev => ({ ...prev, games: { data: games } }));
      toast({
        title: "Sucesso",
        description: `${games?.length || 0} jogos encontrados!`,
      });
    } catch (error) {
      console.error("💥 [Debug] Erro geral:", error);
      setResults(prev => ({ ...prev, games: { error } }));
      toast({
        title: "Erro",
        description: "Falha ao carregar jogos",
        variant: "destructive",
      });
    }
    setLoading("");
  };

  const testFullCommunityLoad = async () => {
    setLoading("full");
    try {
      console.log("🧪 [Debug] Testando carregamento completo da comunidade...");
      
      const community = await fetchCommunityBySlug('rumo-ao-worlds-2025-pokemon-tcg');
      
      if (community) {
        console.log("✅ [Debug] Comunidade completa carregada:", community);
        console.log("👤 [Debug] Dados do mentor:", community.mentor);
        console.log("🖼️ [Debug] Avatar do mentor:", community.mentor?.avatar_url || community.mentor?.image_url || 'NENHUM');
        setResults(prev => ({ ...prev, fullCommunity: { data: community } }));
        toast({
          title: "Sucesso",
          description: "Dados completos carregados!",
        });
      } else {
        console.log("❌ [Debug] Comunidade não encontrada");
        setResults(prev => ({ ...prev, fullCommunity: { error: 'Não encontrada' } }));
        toast({
          title: "Erro",
          description: "Falha no carregamento completo",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("💥 [Debug] Erro no carregamento completo:", error);
      setResults(prev => ({ ...prev, fullCommunity: { error } }));
      toast({
        title: "Erro",
        description: "Falha no carregamento completo",
        variant: "destructive",
      });
    }
    setLoading("");
  };

  const testRouteNavigation = () => {
    console.log('🔗 [CommunityDebug] Testando navegação para editar comunidade...');
    
    const communityId = '9d9c9d44-1234-5678-9abc-def123456789'; // ID de exemplo
    const editUrl = `/painel-mentor/comunidades/${communityId}/editar`;
    const dashUrl = `/painel-mentor/comunidades/${communityId}/dashboard`;
    
    console.log('📝 [CommunityDebug] URL de edição:', editUrl);
    console.log('📊 [CommunityDebug] URL de dashboard:', dashUrl);
    
    // Testar com URL real se existir
    if (results.community?.data.id) {
      const realEditUrl = `/painel-mentor/comunidades/${results.community.data.id}/editar`;
      const realDashUrl = `/painel-mentor/comunidades/${results.community.data.id}/dashboard`;
      
      console.log('🔗 [CommunityDebug] URL real de edição:', realEditUrl);
      console.log('🔗 [CommunityDebug] URL real de dashboard:', realDashUrl);
      
      setResults(prev => ({
        ...prev,
        editUrl: realEditUrl,
        dashUrl: realDashUrl
      }));
    }
    
    toast({
      title: "URLs geradas",
      description: "Verifique o console para ver as URLs de gerenciamento",
    });
  };

  const testMentorLink = () => {
    console.log('👤 [CommunityDebug] Testando link do mentor...');
    
    if (results.community?.data.mentor) {
      const mentor = results.community.data.mentor;
      const mentorUrl = `/mentores/${mentor.slug || mentor.nickname || mentor.id || 'mentor'}`;
      
      console.log('🔗 [CommunityDebug] URL do mentor:', mentorUrl);
      console.log('📋 [CommunityDebug] Dados do mentor:', mentor);
      
      setResults(prev => ({
        ...prev,
        mentorUrl
      }));
      
      toast({
        title: "Link do mentor gerado",
        description: `URL: ${mentorUrl}`,
      });
    } else {
      toast({
        title: "Erro",
        description: "Carregue uma comunidade primeiro",
        variant: "destructive",
      });
    }
  };

  const testMentorImage = async () => {
    setLoading("mentorImage");
    try {
      console.log("🖼️ [Debug] Testando busca da imagem do mentor...");
      
      const mentorId = 'b0e6f620-12b2-487c-a55c-60754ae4d9b9';
      
      // Vamos ver todos os campos disponíveis primeiro
      const { data: mentorProfile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', mentorId)
        .single();
      
      if (error) {
        console.error("❌ [Debug] Erro ao buscar perfil:", error);
        throw error;
      }
      
      console.log("✅ [Debug] Perfil completo:", mentorProfile);
      console.log("✅ [Debug] Todos os campos:", Object.keys(mentorProfile));
      
      // Agora vamos tentar o mesmo método que funciona no useUserProfile
      console.log("🔍 [Debug] Buscando imagem do mentor igual no useUserProfile...");
      
      // Verificar se existe bucket de profile-images
      const { data: bucketData, error: bucketError } = await supabase
        .storage
        .from('profile-images')
        .list(mentorId);
      
      if (bucketError) {
        console.warn("⚠️ [Debug] Erro no bucket profile-images:", bucketError);
      } else {
        console.log("✅ [Debug] Arquivos no bucket profile-images:", bucketData);
        
        if (bucketData && bucketData.length > 0) {
          const imageFile = bucketData.find(file => file.name.startsWith('profile-'));
          if (imageFile) {
            const imageUrl = supabase.storage
              .from('profile-images')
              .getPublicUrl(`${mentorId}/${imageFile.name}`)
              .data.publicUrl;
            
            console.log("🖼️ [Debug] URL da imagem encontrada:", imageUrl);
            setResults(prev => ({ 
              ...prev, 
              mentorImage: { 
                imageUrl,
                fileName: imageFile.name,
                profile: mentorProfile
              } 
            }));
            
            toast({
              title: "Sucesso",
              description: `Imagem do mentor encontrada!`,
            });
            setLoading("");
            return;
          }
        }
      }
      
      console.log("❌ [Debug] Nenhuma imagem encontrada para o mentor");
      setResults(prev => ({ 
        ...prev, 
        mentorImage: { 
          error: "Nenhuma imagem encontrada",
          profile: mentorProfile
        } 
      }));
      
      toast({
        title: "Info",
        description: "Nenhuma imagem encontrada para o mentor",
      });
      
    } catch (error) {
      console.error("💥 [Debug] Erro geral:", error);
      setResults(prev => ({ ...prev, mentorImage: { error } }));
      toast({
        title: "Erro",
        description: "Falha ao buscar imagem do mentor",
        variant: "destructive",
      });
    }
    setLoading("");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Debug das Queries da Comunidade</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Button 
            onClick={testMentorProfile} 
            disabled={loading === "mentor"}
            variant="outline"
          >
            {loading === "mentor" ? "Testando..." : "Testar Perfil Mentor"}
          </Button>
          
          <Button 
            onClick={testCommunityBySlug} 
            disabled={loading === "community"}
            variant="outline"
          >
            {loading === "community" ? "Testando..." : "Testar Comunidade"}
          </Button>
          
          <Button 
            onClick={testGameQuery} 
            disabled={loading === "game"}
            variant="outline"
          >
            {loading === "game" ? "Testando..." : "Testar Game"}
          </Button>
          
          <Button 
            onClick={testFullCommunityLoad} 
            disabled={loading === "full"}
            variant="outline"
          >
            {loading === "full" ? "Testando..." : "Testar Comunidade Completa"}
          </Button>
          
          <Button 
            onClick={testRouteNavigation} 
            disabled={loading === "route"}
            variant="outline"
          >
            {loading === "route" ? "Testando..." : "Testar Rotas"}
          </Button>
          
          <Button 
            onClick={testMentorLink} 
            disabled={loading === "mentor"}
            variant="outline"
          >
            {loading === "mentor" ? "Testando..." : "Testar Link do Mentor"}
          </Button>
          
          <Button 
            onClick={testMentorImage} 
            disabled={loading === "mentorImage"}
            variant="outline"
          >
            {loading === "mentorImage" ? "Testando..." : "Testar Imagem do Mentor"}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          {/* Resultado Perfil Mentor */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Perfil Mentor</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify(results.mentorProfile, null, 2)}
              </pre>
            </CardContent>
          </Card>
          
          {/* Resultado Imagem do Mentor */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Imagem Mentor</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify(results.mentorImage, null, 2)}
              </pre>
            </CardContent>
          </Card>
          
          {/* Resultado Comunidade */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Comunidade</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify(results.community, null, 2)}
              </pre>
            </CardContent>
          </Card>
          
          {/* Resultado Game */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Game</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify(results.games, null, 2)}
              </pre>
            </CardContent>
          </Card>
          
          {/* Resultado Comunidade Completa */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Comunidade Completa</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify(results.fullCommunity, null, 2)}
              </pre>
            </CardContent>
          </Card>
          
          {/* URLs */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">URLs de Teste</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-96 text-gray-300">
                {JSON.stringify({
                  editUrl: results.editUrl,
                  dashUrl: results.dashUrl,
                  mentorUrl: results.mentorUrl
                }, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CommunityDebug; 